import React from "react";
import { Navigate, useLocation } from "react-router-dom";
import { isAdminRole } from "../utils/roles";

const readStoredRole = (): string | null => {
  try {
    const raw = localStorage.getItem("user");
    return raw ? JSON.parse(raw)?.role ?? null : null;
  } catch {
    return null;
  }
};

/**
 * Guards app routes. Redirects to /signin (remembering the attempted path)
 * when there is no access token.
 *
 * Admins and regular users otherwise share the same route set (campaigns,
 * campaign details/review, audience, settings — the backend already scopes
 * these correctly per role: admins see any campaign, non-admins only their
 * own). Two routes are the exceptions:
 *  - `adminOnly`: only the dedicated /admin/* pages — non-admins get bounced
 *    to the regular dashboard.
 *  - `blockAdmin`: only the regular dashboard ("/") — admins get bounced to
 *    their own /admin dashboard instead, since that's their real landing page.
 */
const ProtectedRoute: React.FC<{
  children: React.ReactNode;
  adminOnly?: boolean;
  blockAdmin?: boolean;
}> = ({ children, adminOnly = false, blockAdmin = false }) => {
  const location = useLocation();
  const isAuthenticated = !!localStorage.getItem("accessToken");

  if (!isAuthenticated) {
    return <Navigate to="/signin" replace state={{ from: location }} />;
  }

  const isAdmin = isAdminRole(readStoredRole());

  if (adminOnly && !isAdmin) {
    return <Navigate to="/" replace />;
  }

  if (blockAdmin && isAdmin) {
    return <Navigate to="/admin" replace />;
  }

  return <>{children}</>;
};

export default ProtectedRoute;
